import { useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { Activity, CheckCircle2, ChevronRight, Download, FileText, Filter, History, Loader2, MessageSquare, Plug, Plus, RefreshCcw, Star } from "lucide-react";
import MarkdownMessage from "../../components/MarkdownMessage";
import Button from "../../components/ui/Button";
import { LineChart, BarChart, TableChart } from "../../components/ui/Charts";
import { useDemo } from "../../providers/DemoProvider";
import { formatShortDate, getScopeLabel } from "../../services/demoSelectors";
import type { AnalysisScope, InlineChart, PlatformType } from "../../types/demo";

const presetPrompts = [
  "Where are we losing margin this month?",
  "Show overdue invoices over 30 days and likely cashflow risk.",
  "Which Procore projects are trending over budget?",
  "Compare actual vs budget by project manager.",
  "What should I prioritize this week to improve cash position?",
  "Summarize top 5 financial risks in plain English.",
];

const platformLabel = (platform: PlatformType): string => {
  const labels: Record<PlatformType, string> = {
    xero: "Xero",
    myob: "MYOB",
    quickbooks: "QuickBooks",
    sage: "Sage",
    hubspot: "HubSpot",
    stripe: "Stripe",
    procore: "Procore",
    aroflo: "AroFlo",
  };

  return labels[platform] ?? "Unknown";
};

export default () => {
  const { state, askAi, clearChat, toggleFavorite, convertPromptToReport } = useDemo();
  const navigate = useNavigate();
  const [scope, setScope] = useState<AnalysisScope>(state.chatThreads[0]?.scope ?? "all");
  const [activeTabId, setActiveTabId] = useState<string>(state.chatThreads[0]?.id || "new");
  const [openTabs, setOpenTabs] = useState<string[]>(state.chatThreads.slice(0, 2).map(t => t.id));
  const [expandedConnectorRows, setExpandedConnectorRows] = useState<Record<string, boolean>>({});
  const activeThread = state.chatThreads.find(thread => thread.id === activeTabId);
  const groups = [...state.groups].sort((a, b) => a.name.localeCompare(b.name));
  const ungroupedConnections = state.connections.filter(connection => connection.groupIds.length === 0);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const { register, handleSubmit, reset } = useForm<{ prompt: string }>({
    defaultValues: { prompt: state.pendingPrompt || "" },
  });

  const openThread = (id: string) => {
    const thread = state.chatThreads.find(item => item.id === id);
    setOpenTabs(prev => {
      if (prev.includes(id)) return prev;
      return [...prev, id].slice(-4);
    });
    setActiveTabId(id);
    if (thread) {
      setScope(thread.scope);
    }
  };

  const closeTab = (e: React.MouseEvent, id: string) => {
    e.stopPropagation();
    setOpenTabs(prev => {
      const index = prev.indexOf(id);
      const next = prev.filter(tabId => tabId !== id);
      if (activeTabId === id) {
        const neighbor = next[Math.min(index, next.length - 1)];
        setActiveTabId(neighbor ?? "new");
      }
      return next;
    });
  };

  const startNewChat = () => {
    setActiveTabId("new");
    reset({ prompt: "" });
  };

  const toggleConnectorRow = (key: string) => {
    setExpandedConnectorRows(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const submitPrompt = (prompt: string) => {
    const threadId = activeTabId === "history" || activeTabId === "new"
      ? `thread-${crypto.randomUUID()}`
      : activeTabId;
    const usedThread = askAi(prompt, scope, threadId);
    openThread(usedThread);
    reset({ prompt: "" });
    setTimeout(() => messagesEndRef.current?.scrollIntoView({ behavior: "smooth" }), 100);
  };


  const userMessages = activeThread?.messages ?? [];
  const lastMessage = userMessages[userMessages.length - 1];
  const streamingContentLength = lastMessage?.isStreaming ? lastMessage.content.length : 0;

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "instant" });
  }, [activeTabId]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "instant" });
  }, [streamingContentLength]);

  return (
    <section className="relative flex h-full flex-1 flex-col overflow-hidden bg-white">
      <div className="flex items-center justify-between border-b border-slate-200 bg-slate-50">
        <div className="flex items-center overflow-x-auto no-scrollbar">
          <button
            onClick={startNewChat}
            disabled={openTabs.length >= 4}
            className={`flex items-center gap-2 border-r border-slate-200 px-6 py-3 text-xs font-semibold transition-all ${openTabs.length >= 4 ? "cursor-not-allowed text-slate-200" : activeTabId === "new" ? "border-t-2 border-t-indigo-500 bg-white text-indigo-600 shadow-sm" : "text-slate-400 hover:bg-white/50 hover:text-slate-600"}`}
          >
            <Plus className="h-3.5 w-3.5" /> New Chat
          </button>

          {openTabs.map(tabId => {
            const thread = state.chatThreads.find(item => item.id === tabId);
            if (!thread) return null;
            return (
              <div
                key={tabId}
                onClick={() => openThread(tabId)}
                className={`group relative flex min-w-[140px] max-w-[200px] cursor-pointer items-center gap-3 border-r border-slate-200 px-6 py-3 transition-all ${activeTabId === tabId ? "border-t-2 border-t-indigo-500 bg-white text-indigo-600 shadow-sm" : "text-slate-400 hover:bg-white/50 hover:text-slate-600"}`}
              >
                <MessageSquare className="h-3.5 w-3.5 shrink-0 text-indigo-500/40" />
                <span className="truncate text-xs font-semibold">{thread.title || "Session"}</span>
                <button onClick={e => closeTab(e, tabId)} className="ml-auto opacity-0 transition-opacity group-hover:opacity-100 hover:text-rose-500">
                  <Plus className="h-3 w-3 rotate-45" />
                </button>
              </div>
            );
          })}
        </div>

        <button
          onClick={() => setActiveTabId("history")}
          className={`flex items-center gap-2 border-l border-slate-200 px-8 py-3 text-xs font-semibold transition-all ${activeTabId === "history" ? "border-t-2 border-t-indigo-500 bg-white text-indigo-600 shadow-sm" : "text-slate-400 hover:bg-white/50 hover:text-slate-600"}`}
        >
          <History className="h-4 w-4" /> History
        </button>
      </div>

      {activeTabId === "history" ? (
        <div className="flex flex-1 flex-col overflow-hidden bg-white">
          <header className="flex items-center justify-between border-b border-slate-100 px-8 py-6">
            <div>
              <h2 className="text-xl font-semibold tracking-tight text-slate-900">Chat History</h2>
              <p className="text-xs font-semibold text-slate-400">History</p>
            </div>
          </header>
          <div className="flex-1 overflow-y-auto scroll-smooth">
            <div className="w-full">
              <table className="w-full text-sm">
                <thead className="sticky top-0 border-b border-slate-100 bg-white/95 text-left text-xs font-semibold text-slate-400 backdrop-blur-md">
                  <tr>
                    <th className="w-1/3 px-8 py-4">Session Title</th>
                    <th className="px-8 py-4">Context Overview</th>
                    <th className="px-8 py-4 text-right">Synchronization Date</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-50">
                  {state.chatThreads.map(thread => {
                    const lastMsg = thread.messages[thread.messages.length - 1]?.content || "No records in stream";
                    return (
                      <tr key={thread.id} onClick={() => openThread(thread.id)} className="group cursor-pointer transition-colors hover:bg-slate-50">
                        <td className="px-8 py-6">
                          <div className="flex items-center gap-4">
                            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-slate-100 bg-slate-50 transition-colors group-hover:border-indigo-200">
                              <MessageSquare className="h-4 w-4 text-indigo-400 group-hover:text-indigo-600" />
                            </div>
                            <span className="font-bold text-slate-700 transition-colors group-hover:text-slate-900">{thread.title || "Unidentified Session"}</span>
                          </div>
                        </td>
                        <td className="px-8 py-6">
                          <p className="line-clamp-1 text-xs font-medium text-slate-400 transition-colors group-hover:text-slate-600">{getScopeLabel(state, thread.scope)} • "{lastMsg.slice(0, 100)}..."</p>
                        </td>
                        <td className="px-8 py-6 text-right">
                          <span className="text-xs font-bold text-slate-400">{formatShortDate(thread.updatedAt)}</span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              {state.chatThreads.length === 0 && (
                <div className="flex h-96 flex-col items-center justify-center text-center">
                  <Activity className="mb-4 h-12 w-12 text-slate-200" />
                  <p className="text-xs font-semibold text-slate-300">No Conversations Yet</p>
                  <Button onClick={startNewChat} variant="ghost" className="mt-4 text-xs font-bold">Initialize Node</Button>
                </div>
              )}
            </div>
          </div>
        </div>
      ) : (
        <div className="relative flex flex-1 flex-col overflow-hidden bg-white">
          <header className="flex items-center justify-between border-b border-slate-100 px-4 py-2.5 md:px-5 md:py-3">
            <div className="flex items-center gap-2 md:gap-3">
              <div className="flex h-8 w-8 items-center justify-center rounded-xl border border-indigo-100 bg-indigo-50 text-indigo-600 md:h-10 md:w-10">
                <Activity className="h-4 w-4 md:h-5 md:w-5" />
              </div>
              <div>
                <h2 className="max-w-[170px] truncate text-sm font-semibold tracking-tight text-slate-900 md:max-w-none">{activeThread?.title || "Current Session"}</h2>
                <p className="hidden text-xs font-medium text-slate-400 md:block">{getScopeLabel(state, scope)}</p>
              </div>
            </div>

            <div className="flex items-center gap-2 md:gap-4">
              <Button
                variant="outline"
                size="sm"
                className="h-8 bg-white px-2 text-xs font-medium hover:bg-slate-50 md:px-3"
                onClick={() => clearChat(activeThread?.id)}
                disabled={(activeTabId === "new" && !activeThread) || (activeThread ? activeThread.messages.length === 0 : false)}
              >
                <RefreshCcw className="h-3 w-3 md:mr-1.5 md:h-3.5 md:w-3.5" /> <span className="hidden sm:inline">Clear Chat</span>
              </Button>
              <div className="flex items-center gap-2 rounded-lg border border-slate-200 bg-slate-50 px-2.5 py-1.5">
                <Filter className="h-3.5 w-3.5 text-slate-400" />
                <select value={scope} onChange={event => setScope(event.target.value as AnalysisScope)} className="max-w-[80px] bg-transparent text-xs font-medium text-slate-500 outline-none focus:text-indigo-600 md:max-w-none">
                  <option value="all">All Connections</option>
                  {groups.length > 0 && <option value="__group-header" disabled>--- Groups ---</option>}
                  {groups.map(group => (
                    <option key={group.id} value={group.id}>{group.name}</option>
                  ))}
                  {ungroupedConnections.length > 0 && <option value="__connection-header" disabled>--- Connections ---</option>}
                  {ungroupedConnections.map(connection => (
                    <option key={connection.id} value={connection.id}>{connection.name} ({platformLabel(connection.platform)})</option>
                  ))}
                </select>
              </div>
            </div>
          </header>

          <div className="flex-1 space-y-5 overflow-y-auto bg-slate-50/30 p-4 md:p-5">
            {userMessages.length === 0 && (
              <div className="mx-auto flex h-full max-w-2xl flex-col items-center justify-center text-center">
                <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-2xl border border-slate-100 bg-white">
                  <Activity className="h-6 w-6 text-indigo-600" />
                </div>
                <h3 className="text-lg font-semibold tracking-tight text-slate-900">Quantira Intelligence</h3>
                <p className="mt-1.5 text-xs font-medium text-slate-400">Requesting operational analysis</p>

                <div className="mt-7 grid w-full gap-2.5 sm:grid-cols-2">
                  {presetPrompts.map(prompt => (
                    <button key={prompt} onClick={() => submitPrompt(prompt)} className="group flex items-center justify-between rounded-xl border border-slate-200 bg-white px-3.5 py-3 text-left text-xs font-medium transition-all hover:border-indigo-400">
                      <span className="text-slate-500 group-hover:text-indigo-600">{prompt}</span>
                      <ChevronRight className="h-4 w-4 text-slate-700 transition-transform group-hover:translate-x-1 group-hover:text-indigo-500" />
                    </button>
                  ))}
                </div>
              </div>
            )}

            {userMessages.map((message, index) => {
              if (message.role === "user") {
                return (
                  <div key={message.id} className="flex justify-end animate-in slide-in-from-right-4 fade-in duration-300">
                    <div className="max-w-[90%] rounded-xl rounded-tr-none bg-indigo-600 px-4 py-2.5 text-sm font-medium text-white md:max-w-[75%]">
                      {message.content}
                    </div>
                  </div>
                );
              }

              const previousPrompt = userMessages[index - 1]?.content ?? "";
              const isFavorite = state.favorites.some(item => item.id === message.id);

              return (
                <div key={message.id} className="flex gap-3.5 animate-in slide-in-from-left-4 fade-in duration-500">
                  <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg border border-indigo-100 bg-indigo-50 text-indigo-600">
                    <Activity className="h-4 w-4" />
                  </div>
                  <div className="flex-1 space-y-4">
                    <article className="max-w-[95%] space-y-4">
                      {message.isStreaming && (
                        <div className="inline-flex items-center gap-2 rounded-full border border-slate-200/30 px-3 py-1 text-xs font-medium text-slate-400">
                          <Loader2 className="h-3.5 w-3.5 animate-spin" />
                          {message.toolEvents?.[message.toolEvents.length - 1] ?? "Thinking"}
                        </div>
                      )}

                      {message.connectorEvents && message.connectorEvents.length > 0 && (
                        <div className="space-y-1.5">
                          {message.connectorEvents.map(event => {
                            const rowKey = `${message.id}-${event.id}`;
                            const isExpanded = !!expandedConnectorRows[rowKey];

                            return (
                              <div key={event.id}>
                                <button
                                  type="button"
                                  onClick={() => toggleConnectorRow(rowKey)}
                                  className="inline-flex items-center gap-2 rounded-full border border-slate-200/30 px-4 py-1.5 text-left transition-colors hover:border-slate-200/60"
                                >
                                  <Plug className="h-3.5 w-3.5 shrink-0 text-slate-400" />
                                  <span className="truncate text-xs font-medium text-slate-500">{event.label}</span>
                                  {event.description && <span className="truncate text-[11px] text-slate-400">{event.description}</span>}
                                  {event.status === "running" ? <Loader2 className="h-3 w-3 animate-spin text-indigo-500" /> : <CheckCircle2 className="h-3 w-3 text-emerald-500" />}
                                  <ChevronRight className={`h-3 w-3 shrink-0 text-slate-400 transition-transform ${isExpanded ? "rotate-90" : ""}`} />
                                </button>
                                {isExpanded && (event.query || event.summary) && (
                                  <div className="mt-1 space-y-0.5 pl-4">
                                    {event.query && <p className="text-[11px] font-mono text-slate-500">{event.query}</p>}
                                    {event.summary && <p className="text-[11px] text-slate-400">{event.summary}</p>}
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      )}

                      {message.charts && message.charts.length > 0 && (
                        <div className="space-y-3">
                          {message.charts.map((chart: InlineChart) => (
                            <div key={chart.id} className="max-w-lg overflow-hidden rounded-lg border border-slate-200 bg-white px-4 pb-2 pt-3 shadow-sm">
                              <p className="mb-2 text-xs font-semibold text-slate-500">{chart.title}</p>
                              {chart.type === "line" && <LineChart data={chart.data} />}
                              {chart.type === "bar" && <BarChart data={chart.data} />}
                              {chart.type === "table" && <TableChart data={chart.data} />}
                            </div>
                          ))}
                        </div>
                      )}

                      <MarkdownMessage content={message.content || (message.isStreaming ? "Analyzing..." : "")} />

                      {message.response && message.response.followUps.length > 0 && (
                        <div className="flex flex-wrap items-center gap-1.5 pt-1">
                          {message.response.followUps.slice(0, 2).map(follow => (
                            <button
                              key={follow}
                              onClick={() => submitPrompt(follow)}
                              className="rounded-full border border-slate-200 bg-white px-2.5 py-1 text-[11px] font-medium text-slate-500 transition-all hover:border-indigo-400 hover:text-indigo-600"
                            >
                              {follow}
                            </button>
                          ))}
                        </div>
                      )}

                      {!message.isStreaming && (
                        <div className="flex flex-wrap gap-1.5 overflow-x-auto pt-1 no-scrollbar">
                          <Button variant="ghost" size="sm" className="h-7 px-2 text-[11px] font-medium text-slate-400 hover:text-indigo-600" onClick={() => activeThread && toggleFavorite(activeThread.id, message.id, previousPrompt, message.content)}>
                            <Star className={`mr-1.5 h-3.5 w-3.5 ${isFavorite ? "fill-amber-400 text-amber-400" : ""}`} />
                            {isFavorite ? "Saved" : "Save"}
                          </Button>
                          <Button variant="ghost" size="sm" className="h-7 px-2 text-[11px] font-medium text-slate-400 hover:text-indigo-600" onClick={() => navigator.clipboard.writeText(message.content)}><Download className="mr-1.5 h-3.5 w-3.5" /> Copy</Button>
                          <Button variant="ghost" size="sm" className="h-7 px-2 text-[11px] font-medium text-slate-400 hover:text-indigo-600" onClick={() => activeThread && askAi(previousPrompt, scope, activeThread.id, true)}><RefreshCcw className="mr-1.5 h-3.5 w-3.5" /> Re-Analyze</Button>
                          <Button variant="ghost" size="sm" className="h-7 px-2 text-[11px] font-medium text-slate-400 hover:text-indigo-600" onClick={() => { convertPromptToReport(previousPrompt); navigate("/dashboard/automations"); }}><FileText className="mr-1.5 h-3.5 w-3.5" /> Automate</Button>
                        </div>
                      )}
                    </article>
                  </div>
                </div>
              );
            })}
            <div ref={messagesEndRef} />
          </div>

          <footer className="flex-shrink-0 border-t border-slate-100 bg-white p-3 md:p-4">
            <form onSubmit={handleSubmit(values => submitPrompt(values.prompt))} className="relative mx-auto max-w-5xl">
              <input
                {...register("prompt", { required: true })}
                placeholder="Request analysis..."
                className="h-11 w-full rounded-xl border border-slate-200 bg-slate-50 px-4 pr-28 text-sm font-medium outline-none transition-all placeholder:text-slate-300 focus:border-indigo-500 focus:ring-4 focus:ring-indigo-500/5 md:h-12 md:px-6 md:pr-40"
              />
              <div className="absolute right-1.5 top-1.5 flex gap-2 md:right-2.5">
                <Button type="submit" size="md" className="h-8 px-4 text-xs font-semibold md:h-9 md:px-6">Analyze</Button>
              </div>
            </form>
            <p className="mt-2 hidden text-center text-xs font-medium text-slate-300 md:block">System Connected</p>
          </footer>
        </div>
      )}
    </section>
  );
};
